import { API_BASE_URL } from '../utils/constants';
import axiosInstance from './axiosInstance';

// User Authentication API functions
export const UserService = {
	// Login user
	login: async (email, password) => {
		try {
			const response = await fetch(`${API_BASE_URL}/auth/login`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ email, password })
			});

			const data = await response.json();

			// Return the response data with status for the component to handle
			return {
				status: response.status,
				data: data,
				...data // Spread the response data for backward compatibility
			};
		} catch (error) {
			throw error;
		}
	},

	register: async (firstName, lastName, email, password) => {
		try {
			const response = await fetch(`${API_BASE_URL}/customer/register`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ firstName, lastName, email, password })
			});

			const data = await response.json();

			// Return the response data with status for the component to handle
			return {
				status: response.status,
				data: data,
				...data // Spread the response data for backward compatibility
			};
		} catch (error) {
			throw error;
		}
	},

	resendVerificationCode: async (email) => {
		try {
			const response = await fetch(`${API_BASE_URL}/customer/resend-code`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ email })
			});

			const data = await response.json();

			// Return the response data with status for the component to handle
			return {
				status: response.status,
				data: data,
				...data // Spread the response data for backward compatibility
			};
		} catch (error) {
			throw error;
		}
	},

  // Update customer profile
  updateProfile: async (profileData) => {
    try {
      const formData = new FormData();
      
      // Add text fields
      if (profileData.firstName) {
        formData.append('firstName', profileData.firstName);
      }
      if (profileData.lastName) {
        formData.append('lastName', profileData.lastName);
      }
      
      // Add profile image if provided
      if (profileData.profileImage) {
        formData.append('profileImage', profileData.profileImage);
      }

      const response = await axiosInstance.put('/customer/profile', formData);
      const data = await response.data;
      
      // Return the response data with status for the component to handle
      return {
        status: response.status,
        data: data,
        ...data 
      };
    } catch (error) {
      throw error;
    }
  },

  // Forgot password: request OTP
  requestPasswordReset: async (email) => {
    try {
      const response = await fetch(`${API_BASE_URL}/customer/forgot-password/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      return { status: response.status, data, ...data };
    } catch (error) {
      throw error;
    }
  },

  // Forgot password: verify code (no password change)
  verifyPasswordResetCode: async (email, verificationCode) => {
    try {
      const response = await fetch(`${API_BASE_URL}/customer/forgot-password/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, verificationCode }),
      });
      const data = await response.json();
      return { status: response.status, data, ...data };
    } catch (error) {
      throw error;
    }
  },

  // Forgot password: resend code
  resendPasswordResetCode: async (email) => {
    try {
      const response = await fetch(`${API_BASE_URL}/customer/forgot-password/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      return { status: response.status, data, ...data };
    } catch (error) {
      throw error;
    }
  },

  // Forgot password: reset password
  resetPassword: async ({ email, verificationCode, newPassword, confirmPassword }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/customer/forgot-password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, verificationCode, newPassword, confirmPassword }),
      });
      const data = await response.json();
      return { status: response.status, data, ...data };
    } catch (error) {
      throw error;
    }
  },

	verifyCode: async (email, verificationCode) => {
		try {
			const response = await fetch(`${API_BASE_URL}/customer/verify`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ email, verificationCode })
			});

			const data = await response.json();

			// Return the response data with status for the component to handle
			return {
				status: response.status,
				data: data,
				...data // Spread the response data for backward compatibility
			};
		} catch (error) {
			throw error;
		}
	},

	changePassword: async (oldPassword, newPassword) => {
		try {
			const response = await axiosInstance.put('/customer/change-password', {
				oldPassword,
				newPassword
			});

			// Axios already parses the JSON response, so access data directly
			const data = response.data;

			// Return the response data with status for the component to handle
			return {
				status: response.status,
				data: data,
				...data // Spread the response data for backward compatibility
			};
		} catch (error) {
			throw error;
		}
	}
};

// Local storage helper functions
export const AuthStorage = {
	// Get auth token from localStorage
	getToken: () => {
		return localStorage.getItem('authToken');
	},

	// Set auth token in localStorage
	setToken: (token) => {
		localStorage.setItem('authToken', token);
	},

	// Remove auth token from localStorage
	removeToken: () => {
		localStorage.removeItem('authToken');
		localStorage.removeItem('userData');
	},

	// Store user data in localStorage
	setUserData: (userData) => {
		localStorage.setItem('userData', JSON.stringify(userData));
	},

	// Get user data from localStorage
	getUserData: () => {
		try {
			const userData = localStorage.getItem('userData');
			return userData ? JSON.parse(userData) : null;
		} catch (error) {
			console.error('Error parsing user data:', error);
			return null;
		}
	}
};
